import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUser,
  FiUsers,
  FiLock,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  SubUserForm,
  Table,
} from '../../components';
import TableActions from '../../components/Table/TableActions';
import { usePermission } from '../../hooks/usePermission';
import './style.css';

export default function SubUserManager() {
  const navigate = useNavigate();
  const permissions = usePermission('sub_users');
  const [subUsers, setSubUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubUser, setSelectedSubUser] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Delete modal state
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (permissions.canView) {
      loadSubUsers();
    }
  }, [permissions.canView]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  async function loadSubUsers() {
    if (!permissions.canView) return;
    setLoading(true);
    try {
      const data = await Api.getSubUsers();
      if (Array.isArray(data)) {
        setSubUsers(data);
      } else if (data && data.error) {
        toast.error(data.error);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar usuários.');
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setSelectedSubUser(null);
    setIsModalOpen(true);
  }

  function handleEdit(user) {
    setSelectedSubUser(user);
    setIsModalOpen(true);
  }

  function handleRequestDelete(user) {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      await Api.deleteSubUser(userToDelete.id);
      
      setSubUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      toast.success('usuário excluído com sucesso.');
    } catch (e) {
      toast.error('Erro ao excluir usuário.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCancelDelete() {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  }

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return subUsers.filter((user) => {
      const matchesSearch =
        !term ||
        (user.name || '').toLowerCase().includes(term) ||
        (user.email || '').toLowerCase().includes(term);

      let matchesStatus = true;
      if (statusFilter !== 'all') {
        matchesStatus = user.status === statusFilter;
      }

      return matchesSearch && matchesStatus;
    });
  }, [subUsers, searchTerm, statusFilter]);

  const totalItems = filteredUsers.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + pageSize);

  const columns = [
    {
      id: 'name',
      header: 'Usuário',
      width: '2.4fr',
      cellClassName: 'manager-table-cell-primary',
      render: (user) => (
        <>
          <div className="manager-table-avatar">
            <FiUser size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{user.name}</span>
            <span className="manager-table-subtitle">{user.email}</span>
            {user.cpf && <span className="manager-table-subtitle">{user.cpf}</span>}
          </div>
        </>
      ),
    },
    {
      id: 'companies',
      header: 'Empresas',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: (user) => (
        <span className="manager-table-subtitle">
          {user.companies ? user.companies.length : 0} permitidas
        </span>
      ),
    },
    {
      id: 'access_profile',
      header: 'Perfil',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: (user) => (
        <span className="manager-table-subtitle">
          {user.access_profile === 'admin' ? 'Administrador' : 'Usuário'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: (user) => {
        const isActive = user.status === 'active';
        return (
          <span
            className={`manager-tag ${
              isActive ? 'manager-tag-success' : 'manager-tag-danger'
            }`}
          >
            {isActive ? 'Ativo' : 'Inativo'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell',
      render: (user) => (
        <TableActions>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(user);
            }}
          >
            <FiEdit2 size={16} />
            Editar
          </button>
          <button
            type="button"
            className="action-danger"
            disabled={user.access_profile === 'admin'}
            style={user.access_profile === 'admin' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            title={user.access_profile === 'admin' ? 'Administradores não podem ser excluídos' : 'Excluir'}
            onClick={(e) => {
              e.stopPropagation();
              handleRequestDelete(user);
            }}
          >
            <FiTrash2 size={16} />
            Excluir
          </button>
        </TableActions>
      ),
    },
  ];

  function SubUserManagerSkeletonWrapper() {
    // Reusing CompanyManagerSkeleton as it has a similar structure
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && <SubUserManagerSkeletonWrapper />}

        {!loading && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiUsers size={22} />
                </div>
                <div>
                  <h1>Usuários</h1>
                  <p>Gerencie usuários secundários e suas permissões de acesso.</p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button onClick={handleCreate} className="manager-header-button">
                  <FiPlus size={18} />
                  Novo Usuário
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {subUsers.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredUsers.length} de {subUsers.length} usuários
                  </span>
                )}
              </div>

              <div className="manager-filters-grid">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="user-search"
                      placeholder="Busque por nome ou e-mail"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Status</label>
                  <div className="manager-filter-chips">
                    <button
                      type="button"
                      className={`manager-chip ${
                        statusFilter === 'all' ? 'manager-chip-active' : ''
                      }`}
                      onClick={() => setStatusFilter('all')}
                    >
                      Todos
                    </button>
                    <button
                      type="button"
                      className={`manager-chip ${
                        statusFilter === 'active' ? 'manager-chip-active' : ''
                      }`}
                      onClick={() => setStatusFilter('active')}
                    >
                      Ativos
                    </button>
                    <button
                      type="button"
                      className={`manager-chip ${
                        statusFilter === 'inactive' ? 'manager-chip-active' : ''
                      }`}
                      onClick={() => setStatusFilter('inactive')}
                    >
                      Inativos
                    </button>
                  </div>
                </div>
              </div>
            </Card>

            <Table
              items={paginatedUsers}
              columns={columns}
              totalItems={totalItems}
              page={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              emptyMessage="Nenhum usuário encontrado."
            />
          </>
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedSubUser ? 'Editar usuário' : 'Novo usuário'}
        >
          <SubUserForm
            subUser={selectedSubUser}
            onSuccess={() => {
              setIsModalOpen(false);
              loadSubUsers();
            }}
            onCancel={() => setIsModalOpen(false)}
          />
        </Modal>

        <Modal
          isOpen={isDeleteModalOpen}
          onClose={handleCancelDelete}
          title="Excluir usuário"
        >
          <div className="delete-modal-content">
            <p>
              Tem certeza que deseja excluir o usuário{' '}
              <span className="delete-modal-company-name">
                {userToDelete?.name}
              </span>
              ?
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
              Esta ação não pode ser desfeita e o usuário perderá o acesso
              imediatamente.
            </p>
          </div>
          <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </Modal>
      </div>
    </PageComponent>
  );
}
